import { useEffect, useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  BaseEdge,
  EdgeLabelRenderer,
  Handle,
  Position,
  getBezierPath,
  MarkerType,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type {
  ShoppingSessionPathGraph,
  ShoppingSessionPathGraphEdge,
  ShoppingSessionPathGraphNode,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const NODE_ROW_PX = 68;
const LAYER_GAP_PX = 200;
const CANVAS_MIN = 360;
const CANVAS_MAX = 720;

type NodeData = {
  payload: ShoppingSessionPathGraphNode;
  highlighted: boolean;
  dimmed: boolean;
};

type EdgeData = {
  payload: ShoppingSessionPathGraphEdge;
  highlighted: boolean;
  dimmed: boolean;
};

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

function PathNodeView({ data }: NodeProps<Node<NodeData>>) {
  const n = data.payload;
  const isEnd = n.label === "Zakup" || n.label === "Porzucenie";
  return (
    <div
      className={cn(
        "w-[140px] rounded border px-2 py-1.5 shadow-sm transition",
        n.label === "Zakup" && "border-emerald-600 bg-emerald-50 text-emerald-950",
        n.label === "Porzucenie" && "border-slate-400 bg-slate-50 text-slate-800",
        !isEnd && "border-violet-500 bg-violet-50 text-violet-950",
        data.highlighted && "ring-2 ring-brand ring-offset-1",
        data.dimmed && "opacity-25",
      )}
      title={`${n.label}\n${n.sessions} sesji · CVR ${pct(n.cvr)}`}
    >
      <Handle type="target" position={Position.Left} className="!h-1.5 !w-1.5 !bg-slate-400" />
      <div className="truncate text-[11px] font-semibold leading-tight">{n.label}</div>
      <div className="mt-0.5 font-mono text-[10px] opacity-80">
        {n.sessions} · {pct(n.share)}
      </div>
      <Handle type="source" position={Position.Right} className="!h-1.5 !w-1.5 !bg-slate-400" />
    </div>
  );
}

function PathEdgeView(props: EdgeProps<Edge<EdgeData>>) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    data,
  } = props;
  const d = data!;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const n = d.payload.sessions;
  const stroke = d.highlighted ? "#0f766e" : d.dimmed ? "#cbd5e1" : "#64748b";
  const width = Math.max(1.5, Math.min(8, 1.5 + Math.log10(Math.max(n, 1)) * 2.5));
  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ stroke, strokeWidth: width, opacity: d.dimmed ? 0.35 : 0.9 }}
      />
      <EdgeLabelRenderer>
        <div
          className={cn(
            "nodrag nopan pointer-events-none absolute rounded bg-background/90 px-1 py-0.5 font-mono text-[9px] text-foreground shadow-sm",
            d.dimmed && "opacity-40",
          )}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          {n}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const nodeTypes = { path: PathNodeView };
const edgeTypes = { path: PathEdgeView };

function FitViewOnData({
  nodeCount,
  canvasHeight,
  highlightKey,
}: {
  nodeCount: number;
  canvasHeight: number;
  highlightKey: string;
}) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    if (nodeCount <= 0) return;
    const id = requestAnimationFrame(() => {
      fitView({ padding: 0.12, duration: 0, minZoom: 0.2, maxZoom: 1.2 });
    });
    return () => cancelAnimationFrame(id);
  }, [nodeCount, canvasHeight, highlightKey, fitView]);
  return null;
}

function buildElements(
  graph: ShoppingSessionPathGraph,
  highlightSteps: string[] | null,
) {
  const byLayer: Record<number, ShoppingSessionPathGraphNode[]> = {};
  for (const n of graph.nodes) {
    const layer = Number(n.layer) || 0;
    if (!byLayer[layer]) byLayer[layer] = [];
    byLayer[layer].push(n);
  }
  for (const list of Object.values(byLayer)) {
    list.sort((a, b) => b.sessions - a.sessions || a.label.localeCompare(b.label, "pl"));
  }

  const highlightIds = new Set<string>();
  if (highlightSteps?.length) {
    highlightSteps.forEach((label, i) => {
      highlightIds.add(`L${i}:${label}`);
    });
    for (let i = 0; i < highlightSteps.length - 1; i++) {
      highlightIds.add(`L${i}:${highlightSteps[i]}->L${i + 1}:${highlightSteps[i + 1]}`);
    }
  }

  const flowNodes: Node<NodeData>[] = [];
  let maxLayerCount = 1;
  for (const [layerStr, list] of Object.entries(byLayer)) {
    const layer = Number(layerStr);
    maxLayerCount = Math.max(maxLayerCount, list.length);
    list.forEach((n, i) => {
      const highlighted = highlightIds.has(n.id);
      flowNodes.push({
        id: n.id,
        type: "path",
        position: { x: layer * LAYER_GAP_PX, y: i * NODE_ROW_PX },
        data: {
          payload: n,
          highlighted,
          dimmed: Boolean(highlightSteps?.length && !highlighted),
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
    });
  }

  const maxSessions = Math.max(1, ...graph.edges.map((e) => e.sessions));
  const flowEdges: Edge<EdgeData>[] = graph.edges.map((e) => {
    const highlighted =
      highlightIds.has(e.id) ||
      (highlightIds.has(e.source) && highlightIds.has(e.target));
    const dimmed = Boolean(highlightSteps?.length && !highlighted);
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      type: "path",
      data: { payload: e, highlighted, dimmed },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 14,
        height: 14,
        color: highlighted ? "#0f766e" : "#94a3b8",
      },
      style: {
        strokeWidth: 1.5 + (e.sessions / maxSessions) * 4,
      },
    };
  });

  const canvasHeight = Math.min(
    CANVAS_MAX,
    Math.max(CANVAS_MIN, maxLayerCount * NODE_ROW_PX + 80),
  );

  return { flowNodes, flowEdges, canvasHeight };
}

export default function ShoppingSessionPathGraphView({
  graph,
  highlightSteps,
}: {
  graph: ShoppingSessionPathGraph | undefined;
  highlightSteps?: string[] | null;
}) {
  const highlightKey = (highlightSteps ?? []).join(">");
  const { flowNodes, flowEdges, canvasHeight } = useMemo(() => {
    if (!graph?.nodes?.length) {
      return { flowNodes: [], flowEdges: [], canvasHeight: CANVAS_MIN };
    }
    return buildElements(graph, highlightSteps ?? null);
  }, [graph, highlightSteps]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<EdgeData>>([]);

  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  const empty = !graph || graph.sessions <= 0 || flowNodes.length === 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Graf ścieżek (filtr)</CardTitle>
        <p className="text-xs text-muted-foreground">
          {empty
            ? "Brak ścieżek do narysowania w tym filtrze (okno + wynik)."
            : `${graph.note_pl ?? "Agregat z przefiltrowanych sesji."} · ${graph.sessions} sesji${
                highlightSteps?.length ? " · podświetlona wybrana sesja" : ""
              }`}
        </p>
      </CardHeader>
      {empty ? (
        <CardContent className="pt-0">
          <p className="rounded border border-dashed border-border/70 bg-muted/20 px-3 py-8 text-center text-sm text-muted-foreground">
            Zmień okno lub wynik, żeby zobaczyć agregat przejść z listy sesji.
          </p>
        </CardContent>
      ) : (
        <CardContent className="p-0">
          <div className="w-full border-t border-border/60" style={{ height: canvasHeight }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              fitViewOptions={{ padding: 0.12, minZoom: 0.2, maxZoom: 1.2 }}
              minZoom={0.2}
              maxZoom={1.5}
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
            >
              <FitViewOnData
                nodeCount={flowNodes.length}
                canvasHeight={canvasHeight}
                highlightKey={highlightKey}
              />
              <Background gap={18} size={1} />
              <Controls showInteractive={false} />
            </ReactFlow>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
